import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Drawer,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    LinearProgress,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    AccessTime as AccessTimeIcon,
    AccountBalance as AccountBalanceIcon,
    AdminPanelSettings as AdminIcon,
    Balance as BalanceIcon,
    Block as BlockIcon,
    Cable as CableIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    CreditCard as CreditCardIcon,
    Download as DownloadIcon,
    ErrorOutline as ErrorOutlineIcon,
    FilterAlt as FilterAltIcon,
    HourglassTop as HourglassTopIcon,
    Inventory2 as InventoryIcon,
    Launch as LaunchIcon,
    LockOpen as LockOpenIcon,
    Paid as PaidIcon,
    Person as PersonIcon,
    PlayCircle as PlayCircleIcon,
    Receipt as ReceiptIcon,
    Refresh as RefreshIcon,
    Replay as ReplayIcon,
    RestartAlt as ResetIcon,
    Search as SearchIcon,
    Star as StarIcon,
    Timeline as TimelineIcon,
    TrendingUp as TrendingUpIcon,
    Undo as UndoIcon,
} from '@mui/icons-material';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip as RTooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import ExportFieldSelectionModal from './ExportFieldSelectionModal';

const COLORS = ['#1976d2', '#00bfa5', '#ffb300', '#ef5350', '#8e44ad', '#26a69a', '#3949ab'];

const DEFAULT_FILTERS = {
    startDate: null,
    endDate: null,
    iboNumber: '',
    uplinePlatinumIbo: '',
    cardLast4: '',
    actionTypes: [],
    teamNames: [],
    bundleIds: [],
    assetIds: [],
    source: null,
    page: 0,
    size: 50,
    sortBy: 'actionTime',
    sortDirection: 'DESC',
};

const SOURCE_OPTIONS = [
    { value: null, label: 'All sources' },
    { value: 'CONTENT_API', label: 'Customer Website' },
    { value: 'ADMIN_PORTAL', label: 'Admin Portal' },
];

const CONTENT_EXPORT_FIELD_GROUPS = {
    'Audit Record': [
        { key: 'actionTime', label: 'Time', defaultSelected: true },
        { key: 'periodKey', label: 'Coverage Month', defaultSelected: true },
        { key: 'actionType', label: 'Action', defaultSelected: true },
        { key: 'iboNumber', label: 'IBO Number', defaultSelected: true },
        { key: 'iboName', label: 'IBO Name', defaultSelected: true },
        { key: 'teamName', label: 'Team', defaultSelected: true },
        { key: 'uplinePlatinumIbo', label: 'Upline Platinum IBO', defaultSelected: true },
        { key: 'uplinePlatinumIboName', label: 'Upline Platinum Name', defaultSelected: true },
        { key: 'bundleId', label: 'Bundle ID', defaultSelected: true },
        { key: 'bundleName', label: 'Bundle Name', defaultSelected: true },
        { key: 'assetId', label: 'Asset ID', defaultSelected: true },
        { key: 'assetTitle', label: 'Asset Title', defaultSelected: true },
        { key: 'amount', label: 'Amount', defaultSelected: true },
        { key: 'squareFee', label: 'Square Fee', defaultSelected: true },
        { key: 'netAmount', label: 'Net Amount', defaultSelected: true },
        { key: 'currency', label: 'Currency' },
        { key: 'purchaseType', label: 'Purchase Type', defaultSelected: true },
        { key: 'source', label: 'Source', defaultSelected: true },
    ],
    'Payment & References': [
        { key: 'purchaseId', label: 'Purchase ID', defaultSelected: true },
        { key: 'orderId', label: 'Order ID', defaultSelected: true },
        { key: 'paymentId', label: 'Payment ID', defaultSelected: true },
        { key: 'cardLast4', label: 'Card Last 4', defaultSelected: true },
        { key: 'cardBrand', label: 'Card Brand', defaultSelected: true },
        { key: 'squareEventId', label: 'Square Event ID' },
        { key: 'idempotencyKey', label: 'Idempotency Key' },
        { key: 'correlationId', label: 'Correlation ID' },
        { key: 'actionBy', label: 'Action By' },
        { key: 'details', label: 'Details JSON' },
    ],
};

const ACTION_STATUSES = [
    'PURCHASE_INITIATED', 'PURCHASE_COMPLETED', 'PURCHASE_FAILED',
    'REFUND_INITIATED', 'REFUND_COMPLETED', 'ACCESS_GRANTED',
    'ADMIN_ACCESS_GRANTED', 'ACCESS_REVOKED', 'EXPIRED', 'EXTENDED', 'WEBHOOK_RECEIVED',
];

const statusChipColor = (status) => {
    const s = (status || '').toUpperCase();
    if (s.includes('COMPLETED') || s === 'ACCESS_GRANTED' || s === 'ADMIN_ACCESS_GRANTED' || s === 'EXTENDED') return 'success';
    if (s.includes('FAILED') || s === 'ACCESS_REVOKED' || s === 'EXPIRED') return 'error';
    if (s.includes('REFUND')) return 'warning';
    if (s.includes('INITIATED') || s === 'WEBHOOK_RECEIVED') return 'info';
    return 'default';
};

const TIMELINE_ICON_BY_ACTION = {
    PURCHASE_INITIATED: PlayCircleIcon,
    PURCHASE_COMPLETED: CheckCircleIcon,
    PURCHASE_FAILED: ErrorOutlineIcon,
    ACCESS_GRANTED: LockOpenIcon,
    ADMIN_ACCESS_GRANTED: AdminIcon,
    ACCESS_REVOKED: BlockIcon,
    FCY_ACCESS_CLAIMED: StarIcon,
    REFUND_INITIATED: HourglassTopIcon,
    REFUND_COMPLETED: UndoIcon,
    REFUND_FAILED: ReplayIcon,
    EXPIRED: AccessTimeIcon,
    EXTENDED: AccessTimeIcon,
    WEBHOOK_RECEIVED: CableIcon,
    RECONCILED: BalanceIcon,
};

const eventIcon = (actionType) => TIMELINE_ICON_BY_ACTION[actionType] || TimelineIcon;

// DV-317: every desk sees the same date for a charge. In each viewer's own timezone, a charge just
// after midnight Eastern was 1 Oct in Florida and 30 Sep in California, for the same row.
const ADMIN_TIMEZONE = 'America/Los_Angeles';
const ADMIN_TIMEZONE_LABEL = 'Pacific time';

const FULL_TIMESTAMP_OPTS = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZoneName: 'short',
    timeZone: ADMIN_TIMEZONE,
};

/** "2026-10" -> "October 2026"; a weekly key ("2026-W40") is shown as it is. */
export const coverageLabel = (periodKey) => {
    if (!periodKey) return null;
    const m = /^(\d{4})-(\d{2})$/.exec(periodKey);
    if (!m) return periodKey;
    return new Date(Number(m[1]), Number(m[2]) - 1, 1)
        .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const asUtc = (iso) => {
    if (!iso || typeof iso !== 'string') return iso;
    if (/[Z+\-]\d/.test(iso.slice(-6))) return iso;
    return iso.endsWith('Z') ? iso : iso + 'Z';
};

const formatFullTimestamp = (iso) => {
    if (!iso) return '—';
    const ts = new Date(asUtc(iso));
    if (Number.isNaN(ts.getTime())) return '—';
    return ts.toLocaleString(undefined, FULL_TIMESTAMP_OPTS);
};

const relativeTime = (iso) => {
    if (!iso) return '';
    const ts = new Date(asUtc(iso)).getTime();
    if (Number.isNaN(ts)) return '';
    const diff = Date.now() - ts;
    if (diff < 0) return formatFullTimestamp(iso);
    const sec = Math.floor(diff / 1000);
    if (sec < 45) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hr${hr === 1 ? '' : 's'} ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`;
    return formatFullTimestamp(iso);
};

const safeParseJson = (value) => {
    if (value == null) return null;
    if (typeof value === 'object') return value;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return null;
    try {
        return JSON.parse(trimmed);
    } catch (_) {
        return null;
    }
};

const prettyLabel = (key) => {
    if (!key) return '';
    return key
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (c) => c.toUpperCase());
};

const isPrimitive = (v) => v == null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

const looksLikeIsoTimestamp = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v);

const formatDetailValue = (key, value, formatCurrency, formatDate) => {
    if (value == null || value === '') return '—';
    const k = (key || '').toLowerCase();
    if (typeof value === 'number' && (k.includes('amount') || k.includes('price') || k.includes('total'))) {
        return formatCurrency(value);
    }
    if (looksLikeIsoTimestamp(value)) return formatDate(value);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
};

const TimelineEventDetails = ({ details, formatCurrency, formatDate }) => {
    const parsed = safeParseJson(details);

    if (!parsed) {
        if (!details) return null;
        const text = typeof details === 'string' ? details : JSON.stringify(details);
        return (
            <Box
                sx={{
                    mt: 1,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    fontSize: 12,
                    color: 'text.secondary',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >
                {text}
            </Box>
        );
    }

    if (Array.isArray(parsed)) {
        return (
            <Box sx={{ mt: 1 }}>
                {parsed.map((item, i) => (
                    <TimelineEventDetails
                        key={i}
                        details={item}
                        formatCurrency={formatCurrency}
                        formatDate={formatDate}
                    />
                ))}
            </Box>
        );
    }

    const reason = parsed.reason || parsed.errorMessage || parsed.error || parsed.message;
    const flags = [];
    if (parsed.repayment === true) flags.push({ label: 'Repayment retry', color: 'warning' });
    if (parsed.test === true) flags.push({ label: 'Test mode', color: 'info' });
    if (parsed.manual === true) flags.push({ label: 'Manual entry', color: 'default' });

    const skipKeys = new Set([
        'reason', 'errorMessage', 'error', 'message',
        'repayment', 'test', 'manual',
        'bundleTitle', 'assetTitle',
    ]);

    const entries = Object.entries(parsed).filter(([k, v]) => {
        if (skipKeys.has(k)) return false;
        if (v == null || v === '') return false;
        return true;
    });

    return (
        <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {reason && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        p: 1,
                        borderRadius: 1.5,
                        bgcolor: 'error.lighter',
                        backgroundColor: (theme) => theme.palette.mode === 'light' ? 'rgba(239, 83, 80, 0.08)' : 'rgba(239, 83, 80, 0.16)',
                        border: '1px solid',
                        borderColor: 'error.light',
                    }}
                >
                    <ErrorOutlineIcon fontSize="small" color="error" sx={{ mt: 0.1 }} />
                    <Typography variant="caption" color="error.dark" sx={{ lineHeight: 1.4 }}>
                        {reason}
                    </Typography>
                </Box>
            )}

            {flags.length > 0 && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {flags.map((f) => (
                        <Chip key={f.label} size="small" label={f.label} color={f.color} variant="outlined" />
                    ))}
                </Stack>
            )}

            {entries.length > 0 && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, max-content) minmax(0, 1fr)',
                        columnGap: 1.5,
                        rowGap: 0.5,
                        fontSize: 12,
                    }}
                >
                    {entries.map(([k, v]) => (
                        <React.Fragment key={k}>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                {prettyLabel(k)}
                            </Typography>
                            {isPrimitive(v) ? (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.primary',
                                        fontWeight: 500,
                                        wordBreak: 'break-word',
                                        fontFamily: (k.toLowerCase().includes('id') || k.toLowerCase().includes('key')) ? 'monospace' : 'inherit',
                                    }}
                                >
                                    {formatDetailValue(k, v, formatCurrency, formatDate)}
                                </Typography>
                            ) : (
                                <Box
                                    component="pre"
                                    sx={{
                                        m: 0,
                                        p: 0.75,
                                        borderRadius: 0.75,
                                        bgcolor: 'background.paper',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        fontSize: 11,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        gridColumn: '1 / -1',
                                    }}
                                >
                                    {JSON.stringify(v, null, 2)}
                                </Box>
                            )}
                        </React.Fragment>
                    ))}
                </Box>
            )}
        </Box>
    );
};

const TimelineEventCard = ({
    event,
    isFirst,
    isLast,
    formatCurrency,
    formatDate,
}) => {
    const theme = useTheme();
    const color = statusChipColor(event.actionType);
    const palette = theme.palette[color] || theme.palette.grey;
    const accent = palette.main || theme.palette.grey[400];
    const Icon = eventIcon(event.actionType);

    return (
        <Box sx={{ position: 'relative', display: 'flex', gap: 1.5, pl: 0.5 }}>
            {/* Rail + dot */}
            <Box sx={{ position: 'relative', width: 36, flexShrink: 0 }}>
                {!isFirst && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            height: 14,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 2,
                            bgcolor: 'divider',
                        }}
                    />
                )}
                {!isLast && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 48,
                            bottom: -12,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 2,
                            bgcolor: 'divider',
                        }}
                    />
                )}
                <Avatar
                    sx={{
                        width: 32,
                        height: 32,
                        mt: '14px',
                        bgcolor: accent,
                        boxShadow: `0 0 0 4px ${theme.palette.background.paper}`,
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    <Icon sx={{ fontSize: 18 }} />
                </Avatar>
            </Box>

            {/* Card */}
            <Paper
                elevation={0}
                sx={{
                    flex: 1,
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                    <Chip
                        size="small"
                        label={(event.actionType || '').replace(/_/g, ' ')}
                        color={color}
                        sx={{ fontWeight: 700, letterSpacing: 0.3 }}
                    />
                    <Tooltip title={formatFullTimestamp(event.actionTime)} placement="top" arrow>
                        <Typography variant="caption" color="text.secondary">
                            {relativeTime(event.actionTime)}
                        </Typography>
                    </Tooltip>
                    <Typography variant="caption" color="text.disabled">•</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {event.actionBy || 'SYSTEM'}
                    </Typography>
                </Stack>

                {event.amount != null && Number(event.amount) !== 0 && (
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2, mb: 0.25 }}>
                        {formatCurrency(event.amount)}
                    </Typography>
                )}

                {(event.paymentId || event.orderId) && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                        {event.paymentId && (
                            <Tooltip title={event.paymentId} placement="top" arrow>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Payment ${event.paymentId}`}
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: 11,
                                        maxWidth: '100%',
                                        '& .MuiChip-label': {
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        },
                                    }}
                                />
                            </Tooltip>
                        )}
                        {event.orderId && (
                            <Tooltip title={event.orderId} placement="top" arrow>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Order ${event.orderId}`}
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: 11,
                                        maxWidth: '100%',
                                        '& .MuiChip-label': {
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        },
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Stack>
                )}

                <TimelineEventDetails
                    details={event.details}
                    formatCurrency={formatCurrency}
                    formatDate={formatDate}
                />
            </Paper>
        </Box>
    );
};

const KpiCard = ({ icon: Icon, label, value, color, helper }) => {
    const theme = useTheme();
    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                background: `linear-gradient(135deg, ${color}18 0%, ${color}06 100%)`,
                transition: 'transform .18s ease, box-shadow .18s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 10px 26px -12px ${color}66`,
                },
            }}
        >
            <CardContent sx={{ py: 2.25, px: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: color, width: 40, height: 40 }}>
                        <Icon fontSize="small" />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 600 }}>
                            {label}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{value}</Typography>
                        {helper && <Typography variant="caption" color="text.secondary">{helper}</Typography>}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

const SectionHeader = ({ icon: Icon, title, action }) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.2}>
            {Icon && <Icon sx={{ color: 'primary.main' }} />}
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
        </Stack>
        {action}
    </Stack>
);

const ContentPaymentAuditPage = () => {
    const theme = useTheme();
    const { getAccessToken } = useAuth();
    const API_BASE_URL = config.API_BASE_URL;

    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [timePeriod, setTimePeriod] = useState('DAILY');

    const [reportData, setReportData] = useState({ content: [], totalElements: 0, totalPages: 0 });
    const [analytics, setAnalytics] = useState(null);
    const [revenueTrends, setRevenueTrends] = useState([]);
    const [topAssets, setTopAssets] = useState([]);
    const [topBundles, setTopBundles] = useState([]);
    const [failedPayments, setFailedPayments] = useState([]);
    const [reconciliation, setReconciliation] = useState(null);

    const [selectedTimeline, setSelectedTimeline] = useState(null);
    const [selectedPurchaseId, setSelectedPurchaseId] = useState(null);
    const [purchaseDetail, setPurchaseDetail] = useState(null);
    const [loadingTimeline, setLoadingTimeline] = useState(false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastRefreshed, setLastRefreshed] = useState(null);

    const [availableTeams, setAvailableTeams] = useState([]);
    const [availableBundles, setAvailableBundles] = useState([]);
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportFormat, setExportFormat] = useState('CSV');

    const apiHeaders = useCallback(async () => {
        const token = await getAccessToken();
        if (!token) throw new Error('Authentication token not available');
        return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    }, [getAccessToken]);

    const callApi = useCallback(async (path, { method = 'GET', body } = {}) => {
        const headers = await apiHeaders();
        const res = await fetch(`${API_BASE_URL}${path}`, {
            method, headers, body: body ? JSON.stringify(body) : undefined,
        });
        if (!res.ok) {
            const text = await res.text().catch(() => '');
            throw new Error(`${res.status} ${text}`);
        }
        const data = await res.json().catch(() => ({}));
        return data.data !== undefined ? data.data : data;
    }, [API_BASE_URL, apiHeaders]);

    const fetchAvailableTeams = useCallback(async () => {
        try {
            const data = await callApi('/em/v1/admin/teams');
            const list = Array.isArray(data) ? data : (data?.content || []);
            setAvailableTeams(list.map((t) => t.name).filter(Boolean));
        } catch (err) {
            console.warn('Failed to load teams', err);
        }
    }, [callApi]);

    const fetchAvailableBundles = useCallback(async () => {
        try {
            const data = await callApi('/api/v1/content/bundles?activeOnly=true');
            const list = Array.isArray(data) ? data : [];
            setAvailableBundles(list.map((b) => ({ id: b.id, title: b.title || `Bundle #${b.id}` })));
        } catch (err) {
            console.warn('Failed to load bundles', err);
        }
    }, [callApi]);

    useEffect(() => {
        fetchAvailableTeams();
        fetchAvailableBundles();
    }, [fetchAvailableTeams, fetchAvailableBundles]);

    const fetchAnalytics = useCallback(async () => {
        const data = await callApi('/api/v1/content/admin/payment-audit/analytics', { method: 'POST', body: filters });
        setAnalytics(data);
    }, [callApi, filters]);

    const fetchRevenueTrends = useCallback(async () => {
        const data = await callApi(
            `/api/v1/content/admin/payment-audit/revenue-trends?period=${timePeriod}`,
            { method: 'POST', body: filters },
        );
        setRevenueTrends(Array.isArray(data) ? data : []);
    }, [callApi, filters, timePeriod]);

    const fetchTopAssets = useCallback(async () => {
        const data = await callApi('/api/v1/content/admin/payment-audit/top-assets?limit=10', { method: 'POST', body: filters });
        setTopAssets(Array.isArray(data) ? data : []);
    }, [callApi, filters]);

    const fetchTopBundles = useCallback(async () => {
        const data = await callApi('/api/v1/content/admin/payment-audit/top-bundles?limit=10', { method: 'POST', body: filters });
        setTopBundles(Array.isArray(data) ? data : []);
    }, [callApi, filters]);

    const fetchFailedPayments = useCallback(async () => {
        const data = await callApi('/api/v1/content/admin/payment-audit/failed-payments?limit=20', { method: 'POST', body: filters });
        setFailedPayments(Array.isArray(data) ? data : (data?.content || []));
    }, [callApi, filters]);

    const fetchReport = useCallback(async () => {
        const data = await callApi('/api/v1/content/admin/payment-audit/report', { method: 'POST', body: filters });
        setReportData(data || { content: [], totalElements: 0, totalPages: 0 });
    }, [callApi, filters]);

    const runReconciliation = useCallback(async () => {
        try {
            setLoading(true);
            const data = await callApi('/api/v1/content/admin/payment-audit/reconcile', { method: 'POST', body: filters });
            setReconciliation(data);
        } catch (err) {
            setError(`Reconciliation failed: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [callApi, filters]);

    const refreshDashboard = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            await Promise.all([fetchAnalytics(), fetchRevenueTrends(), fetchTopAssets(), fetchTopBundles(), fetchFailedPayments()]);
            setLastRefreshed(new Date());
        } catch (err) {
            setError(`Failed to load analytics: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [fetchAnalytics, fetchRevenueTrends, fetchTopAssets, fetchTopBundles, fetchFailedPayments]);

    const refreshReport = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            await fetchReport();
            setLastRefreshed(new Date());
        } catch (err) {
            setError(`Failed to load report: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [fetchReport]);

    useEffect(() => {
        if (activeTab === 'dashboard') refreshDashboard();
        else if (activeTab === 'report') refreshReport();
    }, [activeTab, filters, timePeriod, refreshDashboard, refreshReport]);

    const handleRowClick = async (purchaseId) => {
        if (!purchaseId) return;
        setSelectedPurchaseId(purchaseId);
        setLoadingTimeline(true);
        setSelectedTimeline(null);
        setPurchaseDetail(null);
        try {
            const data = await callApi(`/api/v1/content/admin/payment-audit/${purchaseId}/details`);
            if (data && data.timeline) {
                setPurchaseDetail(data);
                setSelectedTimeline(data.timeline);
            } else {
                const timeline = await callApi(`/api/v1/content/admin/payment-audit/${purchaseId}/timeline`);
                setSelectedTimeline(Array.isArray(timeline) ? timeline : (timeline?.events || []));
            }
        } catch (err) {
            setError(`Failed to load purchase details: ${err.message}`);
        } finally {
            setLoadingTimeline(false);
        }
    };

    const openExportModal = (fmt) => {
        setExportFormat(fmt);
        setShowExportModal(true);
    };

    const handleExport = async (selectedFields, format) => {
        try {
            setShowExportModal(false);
            setLoading(true);
            const headers = await apiHeaders();
            const body = { filters, selectedFields, format: format || exportFormat };
            const res = await fetch(`${API_BASE_URL}/api/v1/content/admin/payment-audit/export`, {
                method: 'POST', headers, body: JSON.stringify(body),
            });
            if (!res.ok) {
                const text = await res.text().catch(() => '');
                throw new Error(`Export failed: ${res.status} ${text}`);
            }
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            const fmt = (format || exportFormat || 'CSV').toUpperCase();
            const ext = fmt === 'EXCEL' ? 'xlsx' : 'csv';
            a.href = url;
            a.download = `content-payment-audit-${Date.now()}.${ext}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFilter = (field, value) =>
        setFilters((prev) => ({ ...prev, [field]: value, page: 0 }));

    const handlePageChange = (newPage) =>
        setFilters((prev) => ({ ...prev, page: newPage }));

    const formatCurrency = (v) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);
    const formatDate = (d) => (d ? new Date(asUtc(d)).toLocaleString(undefined, FULL_TIMESTAMP_OPTS) : '—');
    const toDateInput = (iso) => (iso ? iso.slice(0, 10) : '');

    const parseDetailsTitle = (details) => {
        if (!details) return null;
        if (typeof details === 'object') return details.bundleTitle || details.assetTitle || null;
        try {
            const parsed = JSON.parse(details);
            return parsed?.bundleTitle || parsed?.assetTitle || null;
        } catch (_) {
            return null;
        }
    };

    const resolveAssetOrBundleLabel = (row) => {
        if (!row) return '—';
        const title = row.assetTitle || row.bundleName || parseDetailsTitle(row.details);
        if (title) return title;
        if (row.assetId) return `Asset #${row.assetId}`;
        if (row.bundleId != null) return `Bundle #${row.bundleId}`;
        return '—';
    };

    const totalRevenue = Number(analytics?.grossRevenue ?? 0);
    const totalPurchases = Number(analytics?.completedPurchases ?? 0);
    const totalRefunds = Number(analytics?.refundedAmount ?? 0);
    const failedCount = Number(analytics?.failedPurchases ?? 0);
    const refundedCount = Number(analytics?.refundedPurchases ?? 0);
    const netRevenue = Number(analytics?.netRevenue ?? 0);
    const squareFees = Number(analytics?.squareFees ?? 0);
    const purchasesWithoutFee = Number(analytics?.purchasesWithoutFee ?? 0);
    const successRate = totalPurchases + failedCount > 0
        ? (totalPurchases / (totalPurchases + failedCount)) * 100
        : null;

    const purchaseTypeChartData = useMemo(() => {
        const counts = analytics?.purchaseCountByType;
        if (!counts) return [];
        return Object.entries(counts)
            .map(([name, value]) => ({ name, value: Number(value) || 0 }))
            .filter((row) => row.value > 0);
    }, [analytics]);

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    mb: 2.5,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                    color: 'success.contrastText',
                }}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.5}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,.18)' }}>
                            <PaidIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>Content Payment Audit</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.85 }}>Purchases, refunds, reconciliation & revenue trends</Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Chip
                            size="small"
                            icon={<AccessTimeIcon sx={{ color: 'inherit !important' }} />}
                            label={ADMIN_TIMEZONE_LABEL}
                            sx={{ bgcolor: 'rgba(255,255,255,.18)', color: 'inherit', fontWeight: 500 }}
                        />
                        {lastRefreshed && (
                            <Chip
                                size="small"
                                label={`Updated ${lastRefreshed.toLocaleTimeString()}`}
                                sx={{ bgcolor: 'rgba(255,255,255,.18)', color: 'inherit', fontWeight: 500 }}
                            />
                        )}
                        <Button
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                            variant="contained"
                            disabled={loading}
                            onClick={() => (activeTab === 'dashboard' ? refreshDashboard() : refreshReport())}
                            sx={{ bgcolor: 'rgba(255,255,255,.2)', '&:hover': { bgcolor: 'rgba(255,255,255,.3)' } }}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {/* Filters */}
            <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                    <FilterAltIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Filters</Typography>
                </Stack>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            fullWidth size="small" label="Start Date" type="date"
                            InputLabelProps={{ shrink: true }}
                            value={toDateInput(filters.startDate)}
                            onChange={(e) => handleFilter('startDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            fullWidth size="small" label="End Date" type="date"
                            InputLabelProps={{ shrink: true }}
                            value={toDateInput(filters.endDate)}
                            onChange={(e) => handleFilter('endDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            fullWidth size="small" label="IBO Number"
                            value={filters.iboNumber}
                            onChange={(e) => handleFilter('iboNumber', e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            fullWidth size="small" label="Upline Platinum IBO"
                            value={filters.uplinePlatinumIbo}
                            onChange={(e) => handleFilter('uplinePlatinumIbo', e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            fullWidth size="small" label="Card Last 4 Digits"
                            value={filters.cardLast4}
                            onChange={(e) => {
                                const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                                handleFilter('cardLast4', v);
                            }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><CreditCardIcon fontSize="small" /></InputAdornment> }}
                            inputProps={{ maxLength: 4 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="content-source-label">Source</InputLabel>
                            <Select
                                labelId="content-source-label"
                                label="Source"
                                value={filters.source ?? ''}
                                onChange={(e) => handleFilter('source', e.target.value || null)}
                                MenuProps={{
                                    TransitionProps: { timeout: 0 },
                                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                                }}
                            >
                                {SOURCE_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.label} value={opt.value ?? ''}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Autocomplete
                            multiple
                            size="small"
                            options={availableTeams}
                            value={filters.teamNames}
                            onChange={(_, v) => handleFilter('teamNames', v)}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
                                ))
                            }
                            renderInput={(params) => <TextField {...params} label="Teams" placeholder={filters.teamNames.length ? '' : 'All teams'} />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Autocomplete
                            multiple
                            size="small"
                            options={availableBundles}
                            getOptionLabel={(opt) => opt?.title || `Bundle #${opt?.id}`}
                            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                            value={availableBundles.filter((b) => filters.bundleIds.includes(b.id))}
                            onChange={(_, v) => handleFilter('bundleIds', v.map((b) => b.id))}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        size="small"
                                        label={option.title || `Bundle #${option.id}`}
                                        {...getTagProps({ index })}
                                        key={option.id}
                                    />
                                ))
                            }
                            renderInput={(params) => <TextField {...params} label="Bundles" placeholder={filters.bundleIds.length ? '' : 'All bundles'} />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 10 }}>
                        <Autocomplete
                            multiple
                            size="small"
                            options={ACTION_STATUSES}
                            value={filters.actionTypes}
                            onChange={(_, v) => handleFilter('actionTypes', v)}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        size="small"
                                        label={option}
                                        color={statusChipColor(option)}
                                        {...getTagProps({ index })}
                                        key={option}
                                    />
                                ))
                            }
                            renderInput={(params) => <TextField {...params} label="Action Types" />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                        <Button fullWidth variant="outlined" startIcon={<ResetIcon />} onClick={() => setFilters(DEFAULT_FILTERS)}>
                            Reset
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Tabs */}
            <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 2.5 }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ px: 1.5 }}>
                    <Tab value="dashboard" icon={<TrendingUpIcon />} iconPosition="start" label="Dashboard" />
                    <Tab value="report" icon={<ReceiptIcon />} iconPosition="start" label="Detailed Report" />
                    <Tab value="reconcile" icon={<BalanceIcon />} iconPosition="start" label="Reconciliation" />
                </Tabs>
            </Paper>

            {activeTab === 'dashboard' && (
                <>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 6, md: 4, lg: 2 }}>
                            <KpiCard
                                icon={PaidIcon}
                                label="Total Revenue"
                                value={formatCurrency(totalRevenue)}
                                color="#26a69a"
                                helper="Gross"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 4, lg: 2 }}>
                            <KpiCard
                                icon={ReceiptIcon}
                                label="Purchases"
                                value={totalPurchases}
                                color="#1976d2"
                                helper="Completed"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 4, lg: 2 }}>
                            <KpiCard
                                icon={AccountBalanceIcon}
                                label="Refunds"
                                value={formatCurrency(totalRefunds)}
                                color="#ffb300"
                                helper={`${refundedCount} refunded`}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 4, lg: 2 }}>
                            <KpiCard
                                icon={ErrorOutlineIcon}
                                label="Failed"
                                value={failedCount}
                                color="#ef5350"
                                helper="Failed purchases"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 4, lg: 2 }}>
                            <KpiCard
                                icon={TrendingUpIcon}
                                label="Net Revenue"
                                value={formatCurrency(netRevenue)}
                                color="#8e44ad"
                                helper={`Gross − refunds − ${formatCurrency(squareFees)} Square fees${
                                    purchasesWithoutFee > 0 ? ` · ${purchasesWithoutFee} without a fee yet` : ''}`}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 4, lg: 2 }}>
                            <KpiCard
                                icon={CheckCircleIcon}
                                label="Success Rate"
                                value={successRate != null ? `${successRate.toFixed(1)}%` : '—'}
                                color="#3949ab"
                                helper="Completed / (completed + failed)"
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                <SectionHeader
                                    icon={TrendingUpIcon}
                                    title="Revenue Trend"
                                    action={
                                        <FormControl size="small" sx={{ minWidth: 140 }}>
                                            <InputLabel>Period</InputLabel>
                                            <Select
                                                label="Period"
                                                value={timePeriod}
                                                onChange={(e) => setTimePeriod(e.target.value)}
                                                MenuProps={{
                                                    TransitionProps: { timeout: 0 },
                                                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                                                }}
                                            >
                                                <MenuItem value="DAILY">Daily</MenuItem>
                                                <MenuItem value="WEEKLY">Weekly</MenuItem>
                                                <MenuItem value="MONTHLY">Monthly</MenuItem>
                                            </Select>
                                        </FormControl>
                                    }
                                />
                                <ResponsiveContainer width="100%" height={280}>
                                    <LineChart data={revenueTrends}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                        <YAxis tick={{ fontSize: 12 }} />
                                        <RTooltip formatter={(v) => formatCurrency(v)} />
                                        <Legend />
                                        <Line type="monotone" dataKey="revenue" stroke="#1976d2" name="Revenue" strokeWidth={2} dot={{ r: 3 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                <SectionHeader icon={AccountBalanceIcon} title="Purchases by Action Type" />
                                {purchaseTypeChartData.length === 0 ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280, color: 'text.disabled' }}>
                                        <Typography variant="body2">No data for current filters</Typography>
                                    </Box>
                                ) : (
                                    <ResponsiveContainer width="100%" height={280}>
                                        <PieChart>
                                            <Pie
                                                data={purchaseTypeChartData}
                                                cx="50%" cy="50%"
                                                innerRadius={55} outerRadius={95}
                                                paddingAngle={3}
                                                dataKey="value"
                                                label={(e) => `${e.name}: ${e.value}`}
                                            >
                                                {purchaseTypeChartData.map((_, i) => (
                                                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <RTooltip formatter={(v) => `${v} purchases`} />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                <SectionHeader icon={InventoryIcon} title="Top Assets by Revenue" />
                                {(!topAssets || topAssets.length === 0) ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280, color: 'text.disabled' }}>
                                        <Typography variant="body2">No asset purchases in this period</Typography>
                                    </Box>
                                ) : (
                                    <ResponsiveContainer width="100%" height={280}>
                                        <BarChart data={topAssets} margin={{ top: 8, right: 12, left: 8, bottom: 28 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                                            <XAxis
                                                dataKey="title"
                                                tick={{ fontSize: 11 }}
                                                interval={0}
                                                angle={-20}
                                                textAnchor="end"
                                                tickFormatter={(v) => (v && v.length > 18 ? `${v.slice(0, 16)}…` : (v || '—'))}
                                            />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <RTooltip
                                                formatter={(v) => formatCurrency(v)}
                                                labelFormatter={(label, payload) => (payload?.[0]?.payload?.title || label || '—')}
                                            />
                                            <Bar dataKey="revenue" fill="#00bfa5" name="Revenue" radius={[6, 6, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                <SectionHeader icon={InventoryIcon} title="Top Bundles by Revenue" />
                                {(!topBundles || topBundles.length === 0) ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280, color: 'text.disabled' }}>
                                        <Typography variant="body2">No bundle purchases in this period</Typography>
                                    </Box>
                                ) : (
                                    <ResponsiveContainer width="100%" height={280}>
                                        <BarChart data={topBundles} margin={{ top: 8, right: 12, left: 8, bottom: 28 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                                            <XAxis
                                                dataKey="name"
                                                tick={{ fontSize: 11 }}
                                                interval={0}
                                                angle={-20}
                                                textAnchor="end"
                                                tickFormatter={(v) => (v && v.length > 18 ? `${v.slice(0, 16)}…` : (v || '—'))}
                                            />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <RTooltip
                                                formatter={(v) => formatCurrency(v)}
                                                labelFormatter={(label, payload) => (payload?.[0]?.payload?.name || label || '—')}
                                            />
                                            <Bar dataKey="revenue" fill="#8e44ad" name="Revenue" radius={[6, 6, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    <Paper elevation={0} sx={{ mt: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
                        <Box sx={{ p: 2.5, pb: 1 }}>
                            <SectionHeader icon={ErrorOutlineIcon} title="Recent Failed Payments" />
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: 'action.hover' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>IBO</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Asset / Bundle</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Error</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {failedPayments.map((p, idx) => {
                                        const parsedDetails = (() => {
                                            try { return typeof p.details === 'string' ? JSON.parse(p.details) : p.details; } catch (_) { return null; }
                                        })();
                                        const reason = parsedDetails?.reason || p.errorMessage || (typeof p.details === 'string' ? p.details : null);
                                        return (
                                            <TableRow
                                                key={p.id || idx}
                                                hover
                                                onClick={() => handleRowClick(p.purchaseId)}
                                                sx={{ cursor: p.purchaseId ? 'pointer' : 'default' }}
                                            >
                                                <TableCell>{formatDate(p.actionTime)}</TableCell>
                                                <TableCell>{p.iboNumber || '—'}</TableCell>
                                                <TableCell>{resolveAssetOrBundleLabel(p)}</TableCell>
                                                <TableCell align="right">{formatCurrency(p.amount)}</TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" color="error.main">
                                                        {reason || '—'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {failedPayments.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                                                No failed payments
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            )}

            {activeTab === 'report' && (
                <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
                    <Box sx={{ p: 2.5, pb: 1 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1}>
                            <SectionHeader icon={ReceiptIcon} title="Purchase Audit Report" />
                            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                                <Typography variant="caption" color="text.secondary">
                                    Showing {reportData.content.length} of {reportData.totalElements} rows
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={() => openExportModal('CSV')}
                                    disabled={loading}
                                >
                                    Export CSV
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<DownloadIcon />}
                                    onClick={() => openExportModal('EXCEL')}
                                    disabled={loading}
                                >
                                    Export Excel
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: 'action.hover' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                        <Tooltip title="The subscription month a charge pays for. It can differ from the month of the timestamp: a charge just after midnight Eastern is the evening before out west.">
                                            <span>Coverage</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>IBO</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>IBO Name</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Upline IBO</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Asset / Bundle</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Square fee</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Net</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Card</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reportData.content.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        hover
                                        onClick={() => handleRowClick(row.purchaseId)}
                                        sx={{ cursor: row.purchaseId ? 'pointer' : 'default' }}
                                    >
                                        <TableCell>{formatDate(row.actionTime)}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {row.periodKey ? (
                                                <Chip size="small" variant="outlined" color="primary" label={coverageLabel(row.periodKey)} />
                                            ) : '—'}
                                        </TableCell>
                                        <TableCell>{row.iboNumber || '—'}</TableCell>
                                        <TableCell>{row.iboName || '—'}</TableCell>
                                        <TableCell>{row.uplinePlatinumIbo || '—'}</TableCell>
                                        <TableCell>{resolveAssetOrBundleLabel(row)}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={row.actionType} color={statusChipColor(row.actionType)} variant="outlined" />
                                        </TableCell>
                                        <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            {row.squareFee != null ? formatCurrency(row.squareFee)
                                                : row.actionType === 'PURCHASE_COMPLETED' && Number(row.amount) > 0
                                                    ? <Typography variant="caption" color="text.disabled">Pending</Typography>
                                                    : '—'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: row.netAmount != null ? 600 : 400 }}>
                                            {row.netAmount != null ? formatCurrency(row.netAmount) : '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 12 }}>
                                            {row.cardLast4
                                                ? `${row.cardBrand || ''} ****${row.cardLast4}`
                                                : '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.orderId || '—'}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.paymentId || '—'}</TableCell>
                                    </TableRow>
                                ))}
                                {reportData.content.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={13} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                                            No records found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {reportData.totalPages > 1 && (
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                            <Pagination
                                page={filters.page + 1}
                                count={reportData.totalPages}
                                onChange={(_, p) => handlePageChange(p - 1)}
                                color="primary"
                                shape="rounded"
                            />
                        </Box>
                    )}
                </Paper>
            )}

            {activeTab === 'reconcile' && (
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                    <SectionHeader
                        icon={BalanceIcon}
                        title="Square vs Database Reconciliation"
                        action={
                            <Button variant="contained" startIcon={<BalanceIcon />} onClick={runReconciliation} disabled={loading}>
                                Run Reconciliation
                            </Button>
                        }
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Compares Square payments against local content purchase records for the selected date range.
                    </Typography>

                    {reconciliation && (
                        <>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <KpiCard icon={CheckCircleIcon} label="Matched" value={reconciliation.matched ?? 0} color="#26a69a" />
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <KpiCard icon={ErrorOutlineIcon} label="Missing in DB" value={reconciliation.missingInDb?.length ?? 0} color="#ef5350" />
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <KpiCard icon={ErrorOutlineIcon} label="Missing in Square" value={reconciliation.missingInSquare?.length ?? 0} color="#ffb300" />
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <KpiCard icon={BalanceIcon} label="Amount Mismatches" value={reconciliation.amountMismatches?.length ?? 0} color="#8e44ad" />
                                </Grid>
                            </Grid>

                            {reconciliation.discrepancies?.length > 0 && (
                                <TableContainer sx={{ mt: 2.5 }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: 'action.hover' }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Payment ID</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>Square Amount</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>DB Amount</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {reconciliation.discrepancies.map((d, i) => (
                                                <TableRow key={i} hover>
                                                    <TableCell><Chip size="small" label={d.type} color="warning" variant="outlined" /></TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{d.paymentId}</TableCell>
                                                    <TableCell align="right">{formatCurrency(d.squareAmount)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(d.dbAmount)}</TableCell>
                                                    <TableCell>{d.details}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    )}
                </Paper>
            )}

            {/* Purchase detail drawer */}
            <Drawer
                anchor="right"
                open={!!selectedPurchaseId}
                onClose={() => { setSelectedPurchaseId(null); setSelectedTimeline(null); setPurchaseDetail(null); }}
                PaperProps={{ sx: { width: { xs: '100%', md: 600 } } }}
            >
                <Box
                    sx={{
                        px: 2.5,
                        pt: 2.5,
                        pb: 2,
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        bgcolor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                                <ReceiptIcon />
                            </Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                    Purchase Details
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                        Purchase #{selectedPurchaseId}
                                    </Typography>
                                    {purchaseDetail?.status && (
                                        <Chip
                                            size="small"
                                            label={purchaseDetail.status}
                                            color={statusChipColor(purchaseDetail.status)}
                                            sx={{ height: 20, fontSize: 11 }}
                                        />
                                    )}
                                </Stack>
                            </Box>
                        </Stack>
                        <IconButton
                            onClick={() => { setSelectedPurchaseId(null); setSelectedTimeline(null); setPurchaseDetail(null); }}
                            aria-label="Close purchase details"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Box>

                <Box sx={{ p: 2.5, pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {loadingTimeline ? (
                        <Stack alignItems="center" sx={{ py: 6 }}>
                            <CircularProgress />
                        </Stack>
                    ) : (
                        <>
                            {purchaseDetail && (
                                <>
                                    {/* Transaction Information */}
                                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                            <ReceiptIcon fontSize="small" color="primary" />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Transaction Information</Typography>
                                        </Stack>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, max-content) 1fr', columnGap: 2, rowGap: 1, fontSize: 13 }}>
                                            <Typography variant="caption" color="text.secondary">Purchase ID</Typography>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>{purchaseDetail.purchaseId}</Typography>
                                            <Typography variant="caption" color="text.secondary">Order ID</Typography>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>{purchaseDetail.orderId || '—'}</Typography>
                                            <Typography variant="caption" color="text.secondary">Payment ID</Typography>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>{purchaseDetail.paymentId || '—'}</Typography>
                                            <Typography variant="caption" color="text.secondary">Status</Typography>
                                            <Box><Chip size="small" label={purchaseDetail.status || '—'} color={statusChipColor(purchaseDetail.status)} variant="outlined" /></Box>
                                            <Typography variant="caption" color="text.secondary">Source</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                {purchaseDetail.source === 'CONTENT_API' ? 'Customer Website'
                                                    : purchaseDetail.source === 'ADMIN_PORTAL' ? 'Admin Portal'
                                                    : purchaseDetail.source || '—'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">Purchase Date</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatDate(purchaseDetail.purchaseDate)}</Typography>
                                        </Box>
                                    </Paper>

                                    {/* Customer Information */}
                                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                            <PersonIcon fontSize="small" color="primary" />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Customer Information</Typography>
                                        </Stack>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, max-content) 1fr', columnGap: 2, rowGap: 1, fontSize: 13 }}>
                                            <Typography variant="caption" color="text.secondary">IBO Number</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 500 }}>{purchaseDetail.iboNumber || '—'}</Typography>
                                            <Typography variant="caption" color="text.secondary">IBO Name</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 500 }}>{purchaseDetail.iboName || '—'}</Typography>
                                            <Typography variant="caption" color="text.secondary">Team</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 500 }}>{purchaseDetail.teamName || '—'}</Typography>
                                            <Typography variant="caption" color="text.secondary">Upline Platinum IBO</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 500 }}>{purchaseDetail.uplinePlatinumIbo || '—'}</Typography>
                                            <Typography variant="caption" color="text.secondary">Upline Platinum Name</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 500 }}>{purchaseDetail.uplinePlatinumIboName || '—'}</Typography>
                                        </Box>
                                    </Paper>

                                    {/* Content Information */}
                                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                            <InventoryIcon fontSize="small" color="primary" />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Content Information</Typography>
                                        </Stack>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, max-content) 1fr', columnGap: 2, rowGap: 1, fontSize: 13 }}>
                                            {purchaseDetail.assetTitle && (
                                                <>
                                                    <Typography variant="caption" color="text.secondary">Asset</Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>{purchaseDetail.assetTitle}</Typography>
                                                </>
                                            )}
                                            {purchaseDetail.assetType && (
                                                <>
                                                    <Typography variant="caption" color="text.secondary">Asset Type</Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>{purchaseDetail.assetType}</Typography>
                                                </>
                                            )}
                                            {purchaseDetail.bundleName && (
                                                <>
                                                    <Typography variant="caption" color="text.secondary">Bundle</Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>{purchaseDetail.bundleName}</Typography>
                                                </>
                                            )}
                                            <Typography variant="caption" color="text.secondary">Purchase Type</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 500 }}>{purchaseDetail.purchaseType || '—'}</Typography>
                                        </Box>
                                    </Paper>

                                    {/* Payment Details */}
                                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                            <PaidIcon fontSize="small" color="primary" />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Payment Details</Typography>
                                        </Stack>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, max-content) 1fr', columnGap: 2, rowGap: 1, fontSize: 13 }}>
                                            <Typography variant="caption" color="text.secondary">Amount</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 14 }}>{formatCurrency(purchaseDetail.amount)}</Typography>
                                            <Typography variant="caption" color="text.secondary">Currency</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 500 }}>{purchaseDetail.currency || 'USD'}</Typography>
                                            {purchaseDetail.cardLast4 && (
                                                <>
                                                    <Typography variant="caption" color="text.secondary">Card</Typography>
                                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                                        <CreditCardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                            {purchaseDetail.cardBrand || ''} **** **** **** {purchaseDetail.cardLast4}
                                                        </Typography>
                                                    </Stack>
                                                </>
                                            )}
                                        </Box>
                                    </Paper>

                                    {/* Square Data */}
                                    {(purchaseDetail.receiptUrl || purchaseDetail.squareEventId) && (
                                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                                <AccountBalanceIcon fontSize="small" color="primary" />
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Square Data</Typography>
                                            </Stack>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, max-content) 1fr', columnGap: 2, rowGap: 1, fontSize: 13 }}>
                                                {purchaseDetail.squareEventId && (
                                                    <>
                                                        <Typography variant="caption" color="text.secondary">Square Event ID</Typography>
                                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>{purchaseDetail.squareEventId}</Typography>
                                                    </>
                                                )}
                                            </Box>
                                            {purchaseDetail.receiptUrl && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<LaunchIcon />}
                                                    onClick={() => window.open(purchaseDetail.receiptUrl, '_blank', 'noopener,noreferrer')}
                                                    sx={{ mt: 1.5 }}
                                                >
                                                    Open Square Receipt
                                                </Button>
                                            )}
                                        </Paper>
                                    )}
                                </>
                            )}

                            {/* Purchase Timeline */}
                            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                    <TimelineIcon fontSize="small" color="primary" />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Purchase Timeline</Typography>
                                    {Array.isArray(selectedTimeline) && (
                                        <Chip
                                            size="small"
                                            label={`${selectedTimeline.length} event${selectedTimeline.length === 1 ? '' : 's'}`}
                                            variant="outlined"
                                            sx={{ height: 20, fontSize: 11 }}
                                        />
                                    )}
                                </Stack>
                                {(selectedTimeline && selectedTimeline.length > 0) ? (
                                    <Box>
                                        {selectedTimeline.map((ev, idx) => (
                                            <TimelineEventCard
                                                key={ev.id || idx}
                                                event={ev}
                                                isFirst={idx === 0}
                                                isLast={idx === selectedTimeline.length - 1}
                                                formatCurrency={formatCurrency}
                                                formatDate={formatDate}
                                            />
                                        ))}
                                    </Box>
                                ) : (
                                    <Stack alignItems="center" spacing={1} sx={{ py: 4, color: 'text.disabled' }}>
                                        <TimelineIcon fontSize="large" />
                                        <Typography variant="body2">No events found for this purchase</Typography>
                                    </Stack>
                                )}
                            </Paper>
                        </>
                    )}
                </Box>
            </Drawer>

            <ExportFieldSelectionModal
                show={showExportModal}
                onClose={() => setShowExportModal(false)}
                onExport={handleExport}
                format={exportFormat}
                title="Content Payment Export"
                note={null}
                fieldGroups={CONTENT_EXPORT_FIELD_GROUPS}
            />
        </Container>
    );
};

export default ContentPaymentAuditPage;
